import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { LoadingScreen } from './components/LoadingScreen';
import { WelcomeScreen } from './components/WelcomeScreen';
import { Onboarding } from './components/Onboarding';
import { MainLayout } from './components/MainLayout';
import { AuthPage } from './pages/auth/AuthPage';
import { TermsGate, hasAcceptedTerms } from './components/TermsGate';
import { useTelegramUser } from './hooks/useTelegramUser';
import { isTelegramEnv, getStoredToken } from './hooks/useAuth';
import { normalizeLang, t } from './i18n';
import './App.css';

const ONBOARDING_KEY = 'ob_done_v1';

type AppState = 'auth' | 'loading' | 'welcome' | 'onboarding' | 'main';

function App() {
  const isTg = isTelegramEnv();
  const [appState, setAppState] = useState<AppState>(() => {
    if (isTg) return 'loading';
    // Web mode: check for existing JWT
    if (getStoredToken()) return 'main';
    return 'auth';
  });
  const { user, isNewUser } = useTelegramUser();
  const lang = normalizeLang(user?.language_code);
  const [showTerms, setShowTerms] = useState(!hasAcceptedTerms());

  useEffect(() => {
    if (!isTg) return;
    const twa = window.Telegram?.WebApp;
    if (!twa?.initData) return;
    twa.ready();
    twa.expand();
  }, [isTg]);

  const handleLoadingComplete = () => {
    if (isNewUser) {
      setAppState('welcome');
    } else {
      setAppState('main');
    }
  };

  const handleWelcomeContinue = () => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (done) {
      setAppState('main');
    } else {
      setAppState('onboarding');
    }
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setAppState('main');
  };

  const handleWebAuthenticated = () => {
    setAppState('main');
  };

  // Web user info for MainLayout (from localStorage)
  const webUser = !isTg ? (() => {
    try {
      const raw = localStorage.getItem('hits_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })() : null;

  // Merged user object: TG user or web user
  const effectiveUser = isTg ? user : (webUser ? {
    id: webUser.user_id,
    first_name: webUser.first_name,
    last_name: webUser.last_name || '',
    username: webUser.email || '',
    language_code: webUser.language_code || 'ru',
  } : null);

  return (
    <div className="app">
      {showTerms && <TermsGate onAccept={() => setShowTerms(false)} />}
      <AnimatePresence mode="wait">
        {appState === 'auth' && (
          <AuthPage key="auth" onAuthenticated={handleWebAuthenticated} />
        )}

        {appState === 'loading' && (
          <LoadingScreen
            key="loading"
            lang={lang}
            onLoadingComplete={handleLoadingComplete}
          />
        )}

        {appState === 'welcome' && (
          <WelcomeScreen
            key="welcome"
            firstName={user?.first_name || t(lang, 'friend')}
            trialDays={1}
            lang={lang}
            onContinue={handleWelcomeContinue}
          />
        )}

        {appState === 'onboarding' && (
          <Onboarding
            key="onboarding"
            lang={lang}
            onComplete={handleOnboardingComplete}
          />
        )}

        {appState === 'main' && (
          <MainLayout
            key="main"
            user={effectiveUser}
            isNewUser={isNewUser}
            onShowOnboarding={() => setAppState('onboarding')}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
